import { FormLabel, Grid, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { FieldMetaProps } from "formik";
import React, { FocusEvent, useEffect, useMemo, useState } from "react";
import { FieldError } from "./FieldError";
import { OCFormControl } from "./FormControl";

export interface IToggleGroupProps {
  input?: any;
  custom?: any;
  name: string;
  value?: any | null;
  onFocus?: (e: FocusEvent<HTMLButtonElement>) => void;
  onBlur?: (e: FocusEvent<HTMLButtonElement>) => void;
  onChange?: ((value: any) => void) | ((event: React.MouseEvent, value: any) => void);
  label: string;
  className?: string;
  options: { label: string | JSX.Element; value: string | boolean }[];
  allowNull: boolean;
  disabled?: boolean;
  meta?: Partial<FieldMetaProps<any>>;
  showSpinner?: boolean;
  labelSX?: any;
  buttonSX?: any;
  toggleGroupSX?: any;
  exclusive: boolean;
  formik?: boolean;
  size?: "small" | "medium" | "large";
  orientation?: "horizontal" | "vertical";
  color?: "primary" | "secondary" | "success" | "error" | "info" | "warning" | "standard";
}

// SWITCHFIELD COMPONENT
export const ToggleGroup = (props: IToggleGroupProps): JSX.Element => {
  const {
    name,
    options,
    label,
    onFocus,
    onBlur,
    value,
    onChange,
    meta,
    labelSX,
    toggleGroupSX,
    buttonSX,
    exclusive,
    formik,
    size = "small",
    orientation = "horizontal",
    color = "primary",
  } = props;
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [formValue, setFormValue] = useState<any>(value);

  // Track if formik value is dirty.
  const dirty: boolean = useMemo(() => {
    if (!meta) return false;
    const { initialValue, touched } = meta;
    return touched && value !== initialValue ? true : false;
  }, [meta, value]);

  const handleFocus = (e: React.FocusEvent<HTMLButtonElement>): void => {
    setIsFocused(true);
    if (onFocus && typeof onFocus === "function") {
      onFocus(e);
    }
  };
  useEffect(() => {
    if (formValue !== value) {
      setFormValue(value);
    }
  }, [value]);

  const handleBlur = (e: React.FocusEvent<HTMLButtonElement>): void => {
    setIsFocused(false);
    if (onBlur && typeof onBlur === "function") {
      onBlur(e);
    }
  };

  const handleToggle = (event: React.MouseEvent<HTMLElement>, newValue: string | boolean | object): void => {
    if (onChange && typeof onChange === "function") {
      onChange(event, newValue);
      setFormValue(newValue);
    }
  };

  const handleButton = (newValue: string | boolean) => (event: any) => {
    if (onChange && typeof onChange === "function") {
      (onChange as (value: any) => void)(newValue);
    }
  };

  const hasError = meta?.error ? true : false;

  function internalValue(optionValue: any): boolean {
    if (typeof formValue !== "object") {
      return formValue === optionValue;
    }
    if (typeof formValue === "object" && formValue?.length > 0 && formValue[0].hasOwnProperty("value")) {
      return formValue.some((obj: any) => obj.value === optionValue);
    }
    if (typeof formValue === "object" && formValue?.length > 0 && !formValue[0].hasOwnProperty("value")) {
      return formValue.some((obj: any) => obj === optionValue);
    }
    return false;
  }
  return (
    <OCFormControl error={hasError} sx={{ width: "100%", my: 0.3, ...labelSX }}>
      <Grid container sx={{ display: "flex", alignItems: "start" }}>
        {label && label !== "" && (
          <Grid
            item
            sx={{
              maxWidth: "140px",
              display: "flex",
              justifyContent: hasError ? "space-between" : "flex-end",
              alignItems: "center",
              textAlign: "right",
            }}
            xs="auto"
          >
            <FieldError meta={meta} icon={true} />
            <FormLabel>{label}</FormLabel>
          </Grid>
        )}
        <Grid item xs>
          <ToggleButtonGroup
            exclusive={exclusive}
            size={size}
            value={internalValue}
            orientation={orientation}
            classes={{ disabled: "disabled" }}
            onChange={
              formik
                ? null
                : (e, newValue) => {
                    handleToggle(e, newValue);
                  }
            }
            sx={{ ...toggleGroupSX }}
          >
            {options.map((option, key: number) => {
              return (
                <ToggleButton
                  disableRipple
                  sx={{ ...buttonSX }}
                  key={key}
                  id={`${name}-${key}`}
                  value={option.value}
                  onBlur={handleBlur}
                  onFocus={handleFocus}
                  color={color}
                  onClick={formik ? handleButton(option.value) : () => {}}
                  selected={formik ? option.value === value : internalValue(option.value) ? true : false}
                  component={"button"}
                >
                  {option.label}
                </ToggleButton>
              );
            })}
          </ToggleButtonGroup>
        </Grid>
      </Grid>
    </OCFormControl>
  );
};
